// NEW_FILE_JUSTIFIED: Generic batch selection hook — generalizes usePolicySelection for any entity with _id (policy drafts, claim drafts, etc.)

import { useCallback, useState } from 'react';
import type { IUseBatchSelectionResult } from '@/@types/batch';

export function useBatchSelection<T extends { _id: string }>(
  items: T[],
  onDelete: (ids: string[]) => Promise<void>
): IUseBatchSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleSelectRow = useCallback((id: string, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(items.map(item => item._id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [items]
  );

  const handleDeleteConfirm = useCallback(async () => {
    await onDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
    setIsDeleteOpen(false);
  }, [selectedIds, onDelete]);

  const isAllSelected = items.length > 0 && selectedIds.size === items.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;
  const selectedCount = selectedIds.size;

  return {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  };
}
